import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useState, useEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useSelector, useDispatch } from "react-redux";
import {
  DeletePatient,
  GetAllPatients,
  GetTreatmentCompletedPatients,
} from "../../reducer/PatientsSlice";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { GetAllTreatment } from "../../reducer/TreatmentSlice";
import { GetAllCountries2 } from "../../reducer/Countries";
import TableSortLabel from "@mui/material/TableSortLabel";
import axios from "axios";
import { baseurl } from "../../Basurl/Baseurl";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import {
  Button,
  Dialog,
  DialogActions,
  FormControl,
  MenuItem,
  Pagination,
  Select,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import { usePDF } from "react-to-pdf";
import Loader from "../../components/Loader";
import { useSearchParams } from "react-router-dom";
import {
  getPatientStatusLabel,
  isTravelStatus,
  normalizePatientStatus,
  PATIENT_STATUS_OPTIONS,
} from "../../utils/patientStatus";
import {
  getHospitalId,
  isHospitalUser,
  isReferralUser,
  getReferralId,
} from "../../utils/authStorage";
import { hasActivityLogPermission } from "../../utils/sosPermissions";
import { canStaffDelete, canStaffEdit, canStaffExport } from "../../utils/staffActions";
import TravelStatusModal from "../../components/Common/TravelStatusModal";

import {
  buildPatientActivityLogsExcelHtml,
  downloadActivityLogsFile,
  extractActivityLogs,
  extractActivityLogPagination,
  formatActivityLogDate,
  getActivityLogAction,
  getActivityLogDate,
  getActivityLogDescription,
  getActivityLogId,
  getActivityLogModule,
  getActivityLogUser,
} from "../ActivityLog/activityLogHelpers";

const truncateCellText = (text, maxLength = 15) => {
  if (!text) return "";
  const value = String(text);
  return value.length > maxLength
    ? `${value.substring(0, maxLength)}...`
    : value;
};

const getPatientFullPhone = (info) => {
  if (!info) return "—";
  const code = String(info.phoneCode || info.dial_code || "").trim();
  const phone = String(
    info.emergency_contact_no ||
      info.emergency_contact ||
      info.patient_emergency_contact_no ||
      info.phone ||
      info.mobile ||
      info.mobileNumber ||
      "",
  ).trim();
  if (code && phone) {
    if (phone.startsWith(code)) {
      return phone;
    }
    return `${code} ${phone}`;
  }
  return phone || code || "—";
};

const escapeCsvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const escapeHtmlCell = (value) => {
  const text = value == null ? "" : String(value);
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const downloadPatientsExcel = (patientRows = [], isAdmin = true) => {
  const headers = [
    "Sr.No.",
    "Patient ID",
    "Patient Name",
    ...(isAdmin ? ["Phone Number"] : []),
    "Treating In",
    "Date/Time",
    "Country",
    "Patient Type",
    "Status",
  ];
  const headerRow = headers
    .map(
      (header) =>
        `<th style="background-color:#0ba6df;color:#ffffff;font-weight:bold;padding:8px;border:1px solid #ddd;">${escapeHtmlCell(
          header,
        )}</th>`,
    )
    .join("");

  const bodyRows = patientRows
    .map((info, idx) => {
      const phone = getPatientFullPhone(info);
      const createdDate = info.createdAt
        ? `${new Date(info.createdAt).toLocaleDateString("en-GB")} ${new Date(
            info.createdAt,
          ).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}`
        : "";
      const cells = [
        idx + 1,
        info.patientNumber || info.patientId || "",
        info.patient_name || info.name || "",
        ...(isAdmin ? [phone !== "—" ? phone : ""] : []),
        info.treatingIn || "",
        createdDate,
        info.country || "",
        info.patient_type_new || "None",
        getPatientStatusLabel(info.p_status) || info.p_status || "",
      ]
        .map(
          (val) =>
            `<td style="padding:6px;border:1px solid #ddd;">${escapeHtmlCell(
              val,
            )}</td>`,
        )
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const excelHtml = `\uFEFF<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="UTF-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Patients</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
<h3 style="color:#0ba6df;font-family:sans-serif;">Patients List</h3>
<table border="1" style="border-collapse:collapse;font-family:sans-serif;font-size:12px;">
<thead><tr>${headerRow}</tr></thead>
<tbody>${bodyRows}</tbody>
</table>
</body>
</html>`;

  const blob = new Blob([excelHtml], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.setAttribute("download", `patients-${stamp}.xls`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadPatientsCsv = (patientRows = [], isAdmin = true) => {
  const headers = [
    "Sr.No.",
    "Patient ID",
    "Patient Name",
    ...(isAdmin ? ["Phone Number"] : []),
    "Treating In",
    "Date/Time",
    "Country",
    "Patient Type",
    "Status",
  ];
  const lines = [
    headers.join(","),
    ...patientRows.map((info, idx) => {
      const phone = getPatientFullPhone(info);
      const createdDate = info.createdAt
        ? `${new Date(info.createdAt).toLocaleDateString("en-GB")} ${new Date(
            info.createdAt,
          ).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}`
        : "";
      return [
        escapeCsvCell(idx + 1),
        escapeCsvCell(info.patientNumber || info.patientId || ""),
        escapeCsvCell(info.patient_name || info.name || ""),
        ...(isAdmin ? [escapeCsvCell(phone !== "—" ? phone : "")] : []),
        escapeCsvCell(info.treatingIn || ""),
        escapeCsvCell(createdDate),
        escapeCsvCell(info.country || ""),
        escapeCsvCell(info.patient_type_new || "None"),
        escapeCsvCell(
          getPatientStatusLabel(info.p_status) || info.p_status || "",
        ),
      ].join(",");
    }),
  ];
  const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.setAttribute("download", `patients-${stamp}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const PATIENT_TABS = {
  ALL: "all",
  TREATMENT_COMPLETED: "treatment_completed",
};

const EMPTY_PATIENT_FILTERS = {
  patient: "",
  p_status: "",
  patient_type_new: "",
  country: "",
  treatingIn: "",
  treatment: "",
};

const PATIENT_TYPE_FILTER_VALUES = [
  "Private",
  "Foundation",
  "Insurance",
  "Insurance + Private",
];

const autocompleteSx = {
  "& .MuiInputBase-root": { height: 40, backgroundColor: "#fff" },
  "& .MuiInputBase-input": { padding: "6px 12px" },
};

const getTreatmentFilterLabel = (item) =>
  item?.name || item?.course_name || item?.treatment_course_name || "";

const hasAnyPatientFilter = (filters = {}) =>
  Boolean(
    filters.patient ||
      filters.p_status ||
      filters.patient_type_new ||
      filters.country ||
      filters.treatingIn ||
      filters.treatment,
  );

export default function Patient() {
  const role = localStorage.getItem("Role");
  const isAdmin = (role || "").trim().toLowerCase() === "admin";
  const isReferral = isReferralUser();
  const allowEdit = canStaffEdit();
  const allowDelete = canStaffDelete();
  const allowExport = isReferral || canStaffExport();
  const canTrackActivity = hasActivityLogPermission(
    localStorage.getItem("permissionArray") || "",
    role,
  );
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [onVaue, setOnVaue] = useState("");
  const [fullWidth, setFullWidth] = React.useState(true);
  const [maxWidth, setMaxWidth] = React.useState("md");
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPatientId, setActivityPatientId] = useState("");
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [activityTotalRecords, setActivityTotalRecords] = useState(0);
  const [openPatientNotes, setOpenPatientNotes] = useState(false);
  const [notePatientId, setNotePatientId] = useState("");
  const [patientNote, setPatientNote] = useState("");
  const [patientNoteDate, setPatientNoteDate] = useState("");
  const [patientNoteErr, setPatientNoteErr] = useState(false);
  const [patientNoteSubmitting, setPatientNoteSubmitting] = useState(false);
  const [travelStatusModal, setTravelStatusModal] = useState({
    open: false,
    patientId: "",
    patientName: "",
    status: "",
  });
  const [travelStatusSubmitting, setTravelStatusSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const { pagination } = useSelector((state) => state.patient);
  const { patient, loading, error } = useSelector((state) => state.patient);
  const { Treatment } = useSelector((state) => state.Treatment);
  const { Countries } = useSelector((state) => state.Countries);
  const [seekerStatus, setSeekerStatus] = React.useState({});
  const [treatmentname, setTreatmentname] = useState([]);
  const { toPDF, targetRef } = usePDF({ filename: "Patient.pdf" });
  const [selectedJobTitle, setSelectedJobTitle] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [searchApiData, setSearchApiData] = useState([]);
  const [pdfRowLimit, setPdfRowLimit] = useState(null);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("");
  const [report, setReport] = useState({
    country: " ",
    gender: " ",
    age: " ",
  });

  const [appliedFilters, setAppliedFilters] = useState(EMPTY_PATIENT_FILTERS);
  const [patientNameInput, setPatientNameInput] = useState("");
  const appliedPatientRef = useRef("");
  const [activeTab, setActiveTab] = useState(PATIENT_TABS.ALL);
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get("status");
  const typeFromUrl = searchParams.get("type");
  const isTreatmentCompletedTab =
    activeTab === PATIENT_TABS.TREATMENT_COMPLETED;

  const getPatientFetchParams = (overrides = {}) => {
    const refId = isReferralUser() ? getReferralId() : "";
    return {
      page: overrides.page ?? page,
      limit: overrides.limit ?? rowsPerPage,
      search: overrides.search ?? searchTerm,
      p_status: overrides.p_status ?? appliedFilters.p_status,
      patient_type_new:
        overrides.patient_type_new ?? appliedFilters.patient_type_new,
      country: overrides.country ?? appliedFilters.country,
      treatingIn: overrides.treatingIn ?? appliedFilters.treatingIn,
      patient: overrides.patient ?? appliedFilters.patient,
      treatment: overrides.treatment ?? appliedFilters.treatment,
      sortField: overrides.sortField ?? orderBy,
      sortOrder: overrides.sortOrder ?? order,
      ...(refId ? { referral_id: refId } : {}),
      ...(isReferralUser() && overrides.referral_id ? { referral_id: overrides.referral_id } : {}),
    };
  };

  const resolveHospitalId = async () => {
    const cached = getHospitalId();
    if (cached) return cached;
    try {
      const response = await axios.get(
        `${baseurl}get_details/${localStorage.getItem("_id")}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const details = response?.data?.Details || response?.data?.details || {};
      const hospitalId =
        details.hospital_id ??
        details.hospitalId ??
        details.hospital?.hospital_id ??
        details.hospital?.hospitalId ??
        details.hospital?.id ??
        "";
      if (hospitalId !== "" && hospitalId != null) {
        localStorage.setItem("hospital_id", String(hospitalId));
        return String(hospitalId);
      }
    } catch (err) {
      console.error("Failed to resolve hospital id:", err);
    }
    return "";
  };

  const fetchPatientsList = async (overrides = {}) => {
    const params = getPatientFetchParams(overrides);
    if (isTreatmentCompletedTab) {
      const hospitalParams = { ...params };
      delete hospitalParams.p_status;
      delete hospitalParams.patient_type_new;
      delete hospitalParams.country;
      delete hospitalParams.treatingIn;
      delete hospitalParams.treatment;
      delete hospitalParams.patient;
      if (isHospitalUser()) {
        const hospitalId = await resolveHospitalId();
        if (hospitalId) hospitalParams.hospital_id = hospitalId;
      }
      if (isReferralUser()) {
        const refId = getReferralId();
        if (refId) hospitalParams.referral_id = refId;
      } else {
        delete hospitalParams.referral_id;
      }
      return dispatch(GetTreatmentCompletedPatients(hospitalParams));
    }
    return dispatch(GetAllPatients(params));
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setShowActions(true);
    setPage(1);
    appliedPatientRef.current = "";
    setPatientNameInput("");
    setAppliedFilters(EMPTY_PATIENT_FILTERS);
    setOnVaue("");
    navigate("/Admin/patients", { replace: true });
  };

  const handleJobTitleChange = (event, value) => {
    setSelectedJobTitle(value);
  };

  useEffect(() => {
    dispatch(GetAllTreatment());
    dispatch(GetAllCountries2());
  }, [dispatch]);
  useEffect(() => {
    if (Treatment) {
      setTreatmentname(Treatment);
    }
  }, [Treatment]);
  useEffect(() => {
    if (statusFromUrl) {
      const normalized = normalizePatientStatus(statusFromUrl);
      if (normalized === "Treatment Completed") {
        setActiveTab(PATIENT_TABS.TREATMENT_COMPLETED);
        appliedPatientRef.current = "";
        setPatientNameInput("");
        setAppliedFilters(EMPTY_PATIENT_FILTERS);
      } else {
        setActiveTab(PATIENT_TABS.ALL);
        const next = { ...EMPTY_PATIENT_FILTERS, p_status: normalized };
        appliedPatientRef.current = "";
        setPatientNameInput("");
        setAppliedFilters(next);
      }
      setPage(1);
    }
  }, [statusFromUrl]);

  useEffect(() => {
    if (typeFromUrl) {
      setActiveTab(PATIENT_TABS.ALL);
      const next = { ...EMPTY_PATIENT_FILTERS, patient_type_new: typeFromUrl };
      appliedPatientRef.current = "";
      setPatientNameInput("");
      setAppliedFilters(next);
      setPage(1);
    }
  }, [typeFromUrl]);

  useEffect(() => {
    if (!showActions) return;
    fetchPatientsList();
  }, [
    dispatch,
    page,
    rowsPerPage,
    searchTerm,
    appliedFilters,
    orderBy,
    order,
    activeTab,
    showActions,
  ]);
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };
  useEffect(() => {
    // If API returns empty or undefined → clear table
    if (!patient || patient.length === 0) {
      setRows([]);
      setSearchApiData([]);
    } else {
      setRows(
        patient.map((row) => ({
          ...row,
          p_status: normalizePatientStatus(row.p_status) || row.p_status,
        })),
      );
      setSearchApiData(patient);
    }
  }, [patient]);
  useEffect(() => {
    // If API gives 404 or error → clear table
    if (error) {
      setRows([]);
      setSearchApiData([]);
    }
  }, [error]);
  const EditButton = (e, id, rowData) => {
    navigate("/Admin/edit-patient", {
      state: {
        patientId: id,
        patientData: rowData,
      },
    });
  };
  const PatientDetail = (e, id, enq, c, d, treat, createdAt) => {
    console.log(
      e,
      id,
      enq,
      c,
      d,
      "dddddddddddddddddddddddddddddddddddddddddddsh",
    );
    navigate("/Admin/Patient-Detail", {
      state: {
        patientId: id,
        enqId: enq,
        testid: c,
        // tratmentlit: d,
        user_id: d,
        createdAt: createdAt,
      },
    });
  };

  const fetchPatientActivityLogs = async (patientId, pageNum) => {
    if (!patientId) return;
    setActivityLoading(true);
    try {
      const response = await axios.get(
        `${baseurl}get_patient_activity_logs/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          params: { page: pageNum, limit: 25 },
        },
      );

      const logs = extractActivityLogs(response.data);
      const pagination = extractActivityLogPagination(response.data, 25);

      setActivityLogs(logs);
      setActivityPage(pageNum);
      setActivityTotalPages(pagination.totalPages);
      setActivityTotalRecords(pagination.totalRecords);
    } catch (err) {
      setActivityLogs([]);
      setActivityTotalPages(1);
      setActivityTotalRecords(0);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch patient activity logs",
      );
    } finally {
      setActivityLoading(false);
    }
  };

  const handleOpenPatientActivity = async (patientId) => {
    if (!patientId) return;
    setActivityPatientId(patientId);
    setActivityOpen(true);
    setActivityPage(1);
    await fetchPatientActivityLogs(patientId, 1);
  };

  const handleClosePatientActivity = () => {
    setActivityOpen(false);
    setActivityPage(1);
    setActivityTotalPages(1);
    setActivityTotalRecords(0);
    setActivityLogs([]);
  };

  const handleActivityPageChange = (event, newPage) => {
    fetchPatientActivityLogs(activityPatientId, newPage);
  };

  const handleOpenPatientNotes = (e, patientId) => {
    e?.preventDefault?.();
    if (!patientId) return;
    setNotePatientId(patientId);
    setPatientNote("");
    setPatientNoteDate("");
    setPatientNoteErr(false);
    setOpenPatientNotes(true);
  };

  const handleClosePatientNotes = () => {
    setOpenPatientNotes(false);
    setNotePatientId("");
    setPatientNote("");
    setPatientNoteDate("");
    setPatientNoteErr(false);
  };

  const handleSubmitPatientNotes = async (e) => {
    e.preventDefault();
    setPatientNoteErr({
      note: false,
      date: false,
    });
    if (!patientNote) {
      setPatientNoteErr((prev) => ({ ...prev, note: true }));
    }
    if (!patientNoteDate) {
      setPatientNoteErr((prev) => ({ ...prev, date: true }));
    }
    if (!patientNote || !patientNoteDate || !notePatientId) {
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      Swal.fire("Error", "Authorization token is missing", "error");
      return;
    }

    setPatientNoteSubmitting(true);
    try {
      const response = await axios.post(
        `${baseurl}add_patient_notes/${notePatientId}`,
        {
          note: patientNote,
          date: patientNoteDate,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.status === 200 || response.status === 201) {
        handleClosePatientNotes();
        Swal.fire("Success", "Notes added successfully!", "success");
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to add notes",
        "error",
      );
    } finally {
      setPatientNoteSubmitting(false);
    }
  };

  const handledelet = (e, patientId) => {
    e.preventDefault();
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: "btn btn-success",
        cancelButton: "btn btn-danger",
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons
      .fire({
        title: "Are you sure?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
        reverseButtons: true,
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            await dispatch(DeletePatient({ id: patientId })).unwrap();
            await fetchPatientsList();
            Swal.fire("Deleted!", "Patient has been deleted.", "success");
          } catch (err) {
            Swal.fire("Error!", err?.message || "An error occurred", "error");
          }
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          swalWithBootstrapButtons.fire({
            title: "Cancelled",
            icon: "info",
          });
        }
      });
  };
  const handleSampleFile = async () => {
    try {
      const params = getPatientFetchParams({ page: 1, limit: 10000 });
      const queryParams = new URLSearchParams();
      queryParams.append("page", 1);
      queryParams.append("limit", 10000);
      if (params.search) queryParams.append("search", params.search);
      if (params.p_status) queryParams.append("p_status", params.p_status);
      if (params.patient_type_new)
        queryParams.append("patient_type_new", params.patient_type_new);
      if (params.country) queryParams.append("country", params.country);
      if (params.treatingIn) queryParams.append("treatingIn", params.treatingIn);
      if (params.patient) queryParams.append("patient", params.patient);
      if (params.treatment) {
        queryParams.append("treatment", params.treatment);
        queryParams.append("treatment_course_name", params.treatment);
      }
      if (isReferralUser() && params.referral_id) queryParams.append("referral_id", params.referral_id);

      let patientsToExport = rows;
      try {
        const response = await axios.get(
          `${baseurl}all_patients?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const data =
          response?.data?.details ||
          response?.data?.data?.details ||
          response?.data?.patients ||
          response?.data?.data ||
          response?.data;
        if (Array.isArray(data) && data.length > 0) {
          patientsToExport = data;
        }
      } catch (e) {
        console.warn("Using current rows for export:", e);
      }

      if (!patientsToExport || patientsToExport.length === 0) {
        Swal.fire("Info", "No patients available to export", "info");
        return;
      }

      downloadPatientsExcel(patientsToExport, isAdmin);
      Swal.fire("Success", "Patient sheet exported to Excel successfully!", "success");
    } catch (err) {
      console.error(
        "Error downloading patient file:",
        err.response?.data?.message || err.message,
      );
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to export patient sheet",
        "error",
      );
    }
  };
  const handleExportPatientActivityExcel = () => {
    if (!activityLogs || activityLogs.length === 0) {
      Swal.fire("Info", "No activity logs available to export", "info");
      return;
    }
    const excelHtml = `\uFEFF${buildPatientActivityLogsExcelHtml(
      activityLogs,
      activityPatientId,
    )}`;
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `patient-activity-logs-${activityPatientId || "export"}-${stamp}.xls`;
    downloadActivityLogsFile(
      excelHtml,
      filename,
      "application/vnd.ms-excel;charset=utf-8;",
    );
    toast.success("Patient activity logs exported to Excel successfully!");
  };
  const downloadPdf = async () => {
    const maxRows = rows.length || 1;
    Swal.fire({
      title: "Enter number of rows for PDF",
      input: "number",
      inputLabel: `Choose between 1 and ${maxRows}`,
      inputAttributes: {
        min: "1",
        max: maxRows.toString(),
        step: "1",
      },
      inputValue: rowsPerPage,
      showCancelButton: true,
      confirmButtonText: "Generate PDF",
    }).then((result) => {
      if (result.isConfirmed) {
        const userInput = parseInt(result.value, 10);
        if (isNaN(userInput) || userInput < 1 || userInput > maxRows) {
          Swal.fire(
            "Invalid entry",
            `Please enter a number between 1 and ${maxRows}`,
            "error",
          );
          return;
        }
        setPdfRowLimit(userInput);
        setTimeout(() => {
          toPDF();
          setPdfRowLimit(null);
        }, 300);
      }
    });
  };
  const handleChangtype = async (e, i) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authorization token is missing");
      }
      const response = await axios.post(
        `${baseurl}changePatientTypeStatus/${i}`,
        { p_status: e.target.value },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.status === 200 || response.status === 201) {
        fetchPatientsList();
        Swal.fire(
          "Success!",
          "patient status updated successfully!",
          "success",
        );
      } else {
        throw new Error("Failed to update status. Please try again!");
      }
    } catch (err) {
      console.error("Full Error:", err);
      if (err.response && err.response.data && err.response.data.message) {
        toast.error(err.response.data.message);
      } else if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again!");
      }
    }
  };
  const applyPatientStatusUpdate = (patientId, savedStatus) => {
    setRows((prev) =>
      prev.map((row) =>
        row.patientId === patientId ? { ...row, p_status: savedStatus } : row,
      ),
    );
    fetchPatientsList();
  };

  const postPatientStatus = async (patientId, payload) => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authorization token is missing");
    }

    const formData = new FormData();
    const statusVal = payload.p_status || payload.status;
    if (statusVal) {
      formData.append("p_status", statusVal);
    }
    if (payload.travelDetails) {
      const detailsStr = typeof payload.travelDetails === "object"
        ? JSON.stringify(payload.travelDetails)
        : payload.travelDetails;
      formData.append("travelDetails", detailsStr);
    }
    if (payload.travelDocuments) {
      const docs = Array.isArray(payload.travelDocuments)
        ? payload.travelDocuments
        : [payload.travelDocuments];
      docs.forEach((file) => {
        if (file) {
          formData.append("travelDocuments", file);
        }
      });
    }
    if (payload.travelDocumentLabels) {
      if (Array.isArray(payload.travelDocumentLabels)) {
        if (payload.travelDocumentLabels.length === 1) {
          formData.append("travelDocumentLabels", payload.travelDocumentLabels[0]);
        } else {
          formData.append("travelDocumentLabels", JSON.stringify(payload.travelDocumentLabels));
        }
      } else {
        formData.append("travelDocumentLabels", payload.travelDocumentLabels);
      }
    }

    const response = await axios.post(
      `${baseurl}changePatientStatus/${patientId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error("Failed to update status. Please try again!");
    }
    return normalizePatientStatus(
      response.data?.data?.p_status ??
        response.data?.p_status ??
        response.data?.details?.p_status ??
        statusVal,
    );
  };

  const handleChangefffff = async (e, patientId, patientName = "") => {
    if (isReferral) return;
    const newStatus = normalizePatientStatus(e.target.value);
    if (isTravelStatus(newStatus)) {
      setTravelStatusModal({
        open: true,
        patientId,
        patientName:
          patientName ||
          rows.find((row) => row.patientId === patientId)?.patient_name ||
          rows.find((row) => row.patientId === patientId)?.name ||
          "",
        status: newStatus,
      });
      return;
    }
    try {
      const savedStatus = await postPatientStatus(patientId, {
        p_status: newStatus,
      });
      applyPatientStatusUpdate(patientId, savedStatus);
      Swal.fire(
        "Success!",
        "patient status updated successfully!",
        "success",
      );
    } catch (err) {
      console.error("Full Error:", err);
      if (err.response && err.response.data && err.response.data.message) {
        toast.error(err.response.data.message);
      } else if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again!");
      }
    }
  };

  const handleCloseTravelStatusModal = () => {
    if (travelStatusSubmitting) return;
    setTravelStatusModal({
      open: false,
      patientId: "",
      patientName: "",
      status: "",
    });
  };

  const handleSubmitTravelStatus = async (payload) => {
    const patientId = travelStatusModal.patientId;
    if (!patientId) return;
    setTravelStatusSubmitting(true);
    try {
      const savedStatus = await postPatientStatus(patientId, payload);
      applyPatientStatusUpdate(patientId, savedStatus);
      setTravelStatusModal({
        open: false,
        patientId: "",
        patientName: "",
        status: "",
      });
      Swal.fire(
        "Success!",
        "patient status updated successfully!",
        "success",
      );
    } catch (err) {
      console.error("Full Error:", err);
      if (err.response && err.response.data && err.response.data.message) {
        toast.error(err.response.data.message);
      } else if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again!");
      }
    } finally {
      setTravelStatusSubmitting(false);
    }
  };
  const handleclickondata = () => {
    setShowActions(true);
    fetchPatientsList();
  };
  const handleclickpostdatadesltes = async () => {
    setShowActions(false);
    try {
      const response = await axios.get(`${baseurl}getAllDeletedPatients`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (response) {
        setRows(response.data.data);
      } else {
        console.log("something went wrong");
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = patientNameInput.trim();
      if (appliedPatientRef.current === next) return;
      appliedPatientRef.current = next;
      if (next) setActiveTab(PATIENT_TABS.ALL);
      setAppliedFilters((prev) => ({ ...prev, patient: next }));
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [patientNameInput]);

  const updatePatientFilter = (field, value) => {
    if (field === "p_status" && value === "Treatment Completed") {
      setActiveTab(PATIENT_TABS.TREATMENT_COMPLETED);
      appliedPatientRef.current = "";
      setPatientNameInput("");
      setAppliedFilters(EMPTY_PATIENT_FILTERS);
      setPage(1);
      navigate("/Admin/patients", { replace: true });
      return;
    }
    if (value) setActiveTab(PATIENT_TABS.ALL);
    setAppliedFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearPatientFilter = () => {
    appliedPatientRef.current = "";
    setPatientNameInput("");
    setAppliedFilters(EMPTY_PATIENT_FILTERS);
    setOnVaue("");
    setPage(1);
    navigate("/Admin/patients", { replace: true });
  };
  const hasActiveFilter =
    hasAnyPatientFilter(appliedFilters) || Boolean(patientNameInput.trim());
  const countryFilterOptions = [
    ...new Set((Countries || []).map((con) => con?.name).filter(Boolean)),
  ];
  const treatmentFilterOptions = [
    ...new Set(
      (treatmentname || [])
        .map((item) => getTreatmentFilterLabel(item))
        .filter(Boolean),
    ),
  ];
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const direction = isAsc ? "desc" : "asc";

    setOrder(direction);
    setOrderBy(property);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row gx-3">
            <div className="col-md-12">
              <div className="country-top">
                <div className="topmainhd mb-0">
                  <h6>Manage Patients</h6>
                </div>
                <div className="patient-top-btn">
                  <div className="">
                    <TextField
                      className="field-count"
                      label="Search"
                      id="outlined-required"
                      size="small"
                      value={searchTerm}
                      onChange={handleSearch}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {searchTerm && (
                              <IconButton
                                onClick={() => setSearchTerm("")}
                                className="input-set"
                              >
                                <ClearIcon />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  {allowExport && (
                  <button onClick={handleSampleFile} className="add-button" title="Export to Excel">
                    <span>
                      <i className="fa fa-file-excel-o me-2"></i>
                    </span>
                    Export Excel
                  </button>
                  )}
                  {role === "Admin" ? (
                    <button onClick={downloadPdf} className="add-button">
                      <span>
                        <i className="fa fa-file-pdf-o me-2"></i>
                      </span>
                      PDF
                    </button>
                  ) : (
                    ""
                  )}
                  {localStorage.getItem("Role") === "Admin" ? (
                    showActions === true ? (
                      <button
                        className="add-button"
                        onClick={handleclickpostdatadesltes}
                      >
                        Deleted Data
                      </button>
                    ) : (
                      <button
                        className="add-button"
                        onClick={handleclickondata}
                      >
                        Patient
                      </button>
                    )
                  ) : null}
                </div>
              </div>
            </div>
            {showActions ? (
              <div className="col-md-12" style={{ marginBottom: 18 }}>
                <div className="main_content">
                  <div className="row gx-3">
                    <div className="col-md-3">
                      <div className="field-set">
                        <label>Patient Name</label>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Enter patient name"
                          value={patientNameInput}
                          onChange={(e) => setPatientNameInput(e.target.value)}
                          sx={autocompleteSx}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="field-set">
                        <label>Status</label>
                        <Autocomplete
                          options={PATIENT_STATUS_OPTIONS}
                          value={
                            PATIENT_STATUS_OPTIONS.find(
                              (option) => option.value === appliedFilters.p_status,
                            ) || null
                          }
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                          }
                          onChange={(_, newValue) =>
                            updatePatientFilter("p_status", newValue?.value || "")
                          }
                          ListboxProps={{ style: { maxHeight: 250 } }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Search Status"
                              sx={autocompleteSx}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="field-set">
                        <label>Patient Type</label>
                        <Autocomplete
                          options={PATIENT_TYPE_FILTER_VALUES}
                          value={appliedFilters.patient_type_new || null}
                          onChange={(_, newValue) =>
                            updatePatientFilter("patient_type_new", newValue || "")
                          }
                          ListboxProps={{ style: { maxHeight: 250 } }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Search Patient Type"
                              sx={autocompleteSx}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="field-set">
                        <label>Country</label>
                        <Autocomplete
                          options={countryFilterOptions}
                          value={appliedFilters.country || null}
                          onChange={(_, newValue) =>
                            updatePatientFilter("country", newValue || "")
                          }
                          ListboxProps={{ style: { maxHeight: 250 } }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Search Country"
                              sx={autocompleteSx}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="field-set">
                        <label>Treating In</label>
                        <Autocomplete
                          options={countryFilterOptions}
                          value={appliedFilters.treatingIn || null}
                          onChange={(_, newValue) =>
                            updatePatientFilter("treatingIn", newValue || "")
                          }
                          ListboxProps={{ style: { maxHeight: 250 } }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Search Treating In"
                              sx={autocompleteSx}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="field-set">
                        <label>Treatment</label>
                        <Autocomplete
                          options={treatmentFilterOptions}
                          value={appliedFilters.treatment || null}
                          onChange={(_, newValue) =>
                            updatePatientFilter("treatment", newValue || "")
                          }
                          ListboxProps={{ style: { maxHeight: 250 } }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Search Treatment"
                              sx={autocompleteSx}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {hasActiveFilter ? (
                      <div className="col-md-auto">
                        <div className="field-set">
                          <label style={{ display: "block", visibility: "hidden" }}>
                            Clear
                          </label>
                          <button
                            type="button"
                            className="add-button"
                            onClick={clearPatientFilter}
                            style={{
                              height: 40,
                              padding: "0 16px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <i className="fa fa-times"></i>
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            <div className="col-md-12">
              <div
                className="tabs-container"
                style={{
                  marginTop: 4,
                  marginBottom: 16,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => handleTabChange(PATIENT_TABS.ALL)}
                  className={`tab-btn ${
                    activeTab === PATIENT_TABS.ALL ? "active-tab" : ""
                  }`}
                  style={{
                    padding: "8px 18px",
                    border: "1px solid #ccc",
                    background:
                      activeTab === PATIENT_TABS.ALL ? "#22c7b8" : "#fafafa",
                    color: activeTab === PATIENT_TABS.ALL ? "#fff" : "#666",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "12px",
                    lineHeight: "22px",
                    fontWeight: "500",
                  }}
                >
                  All Patients
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleTabChange(PATIENT_TABS.TREATMENT_COMPLETED)
                  }
                  className={`tab-btn ${
                    activeTab === PATIENT_TABS.TREATMENT_COMPLETED
                      ? "active-tab"
                      : ""
                  }`}
                  style={{
                    padding: "8px 18px",
                    border: "1px solid #ccc",
                    background:
                      activeTab === PATIENT_TABS.TREATMENT_COMPLETED
                        ? "#22c7b8"
                        : "#fafafa",
                    color:
                      activeTab === PATIENT_TABS.TREATMENT_COMPLETED
                        ? "#fff"
                        : "#666",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "12px",
                    lineHeight: "22px",
                    fontWeight: "500",
                  }}
                >
                  Treatment Completed
                </button>
              </div>
            </div>
            <div className="col-md-12">
              <div className="main_content">
                <div className="table-responsive" ref={targetRef}>
                  <TableContainer
                    component={Paper}
                    style={{ overflowX: "auto" }}
                  >
                    <Table
                      stickyHeader
                      aria-label="sticky table"
                      className="table-no-card"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Sr.No.</TableCell>
                          {showActions === false ? (
                            <>
                              <TableCell>Deleted By</TableCell>
                              <TableCell>Deleted BY Email</TableCell>
                              <TableCell>Deleted Time</TableCell>
                              <TableCell>Deleted Date</TableCell>
                            </>
                          ) : (
                            ""
                          )}
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === "patientId"}
                              direction={
                                orderBy === "patientId" ? order : "asc"
                              }
                              onClick={() => handleRequestSort("patientId")}
                            >
                              Patient Id
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === "patient_name"}
                              direction={
                                orderBy === "patient_name" ? order : "asc"
                              }
                              onClick={() => handleRequestSort("patient_name")}
                            >
                              Patient Name
                            </TableSortLabel>
                          </TableCell>
                          {isAdmin && <TableCell>Phone Number</TableCell>}
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === "treatingIn"}
                              direction={
                                orderBy === "treatingIn" ? order : "asc"
                              }
                              onClick={() => handleRequestSort("treatingIn")}
                            >
                              Treating In
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === "createdAt"}
                              direction={
                                orderBy === "createdAt" ? order : "asc"
                              }
                              onClick={() => handleRequestSort("createdAt")}
                            >
                              Date/Time
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === "country"}
                              direction={orderBy === "country" ? order : "asc"}
                              onClick={() => handleRequestSort("country")}
                            >
                              Country
                            </TableSortLabel>
                          </TableCell>
                          {showActions === true ? (
                            <>
                              <TableCell>
                                <TableSortLabel
                                  active={orderBy === "patient_type_new"}
                                  direction={
                                    orderBy === "patient_type_new"
                                      ? order
                                      : "asc"
                                  }
                                  onClick={() =>
                                    handleRequestSort("patient_type_new")
                                  }
                                >
                                  Patient Type
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>
                                <TableSortLabel
                                  active={orderBy === "p_status"}
                                  direction={
                                    orderBy === "p_status" ? order : "asc"
                                  }
                                  onClick={() => handleRequestSort("p_status")}
                                >
                                  Status
                                </TableSortLabel>
                              </TableCell>
                              <TableCell>Action</TableCell>
                            </>
                          ) : (
                            ""
                          )}
                        </TableRow>
                      </TableHead>
                      {loading ? (
                        <Loader />
                      ) : (
                        <TableBody>
                          {rows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={15} align="center">
                                No data found
                              </TableCell>
                            </TableRow>
                          ) : (
                            rows.map((info, i) => {
                              return (
                                <TableRow key={info.patientId}>
                                  <TableCell>
                                    {(page - 1) * rowsPerPage + i + 1}
                                  </TableCell>
                                  {showActions === false ? (
                                    <>
                                      <TableCell>
                                        {info?.deletedBy?.name}
                                      </TableCell>
                                      <TableCell>
                                        {info?.deletedBy?.email}
                                      </TableCell>
                                      <TableCell>
                                        {new Date(
                                          info.deletedAt,
                                        ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </TableCell>
                                      <TableCell>
                                        {new Date(
                                          info?.deletedAt,
                                        ).toLocaleDateString("en-GB")}
                                      </TableCell>
                                    </>
                                  ) : (
                                    ""
                                  )}
                                  <TableCell>
                                    {info.patientNumber || info.patientId}
                                  </TableCell>
                                  <TableCell
                                    style={{ cursor: "pointer" }}
                                    onClick={(e) =>
                                      PatientDetail(
                                        e,
                                        info.patientId,
                                        info.enquiryId,
                                        info.id,
                                        info.user_id,
                                        null,
                                        info.createdAt,
                                      )
                                    }
                                    title={info.patient_name}
                                  >
                                    {truncateCellText(info.patient_name)}
                                  </TableCell>
                                   {isAdmin && (
                                     <TableCell
                                       title={getPatientFullPhone(info)}
                                     >
                                       {getPatientFullPhone(info)}
                                     </TableCell>
                                   )}
                                  <TableCell title={info.treatingIn}>
                                    {truncateCellText(info.treatingIn)}
                                  </TableCell>
                                  <TableCell>
                                    {new Date(
                                      info.createdAt,
                                    ).toLocaleDateString("en-GB")}
                                    /
                                    {new Date(
                                      info.createdAt,
                                    ).toLocaleTimeString("en-GB", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })}
                                  </TableCell>
                                  {/* <TableCell>{info.createdBy}</TableCell> */}
                                  <TableCell title={info.country}>
                                    {truncateCellText(info.country)}
                                  </TableCell>
                                  {/* <TableCell>{info.country}</TableCell> */}
                                  {showActions === true ? (
                                    <>
                                      <TableCell>
                                        <FormControl
                                          sx={{ m: 1, minWidth: 120 }}
                                          size="small"
                                          className="cont-main"
                                        >
                                          <Select
                                            value={info.patient_type_new}
                                            onChange={(e) =>
                                              handleChangtype(e, info.patientId)
                                            }
                                            displayEmpty
                                            inputProps={{
                                              "aria-label": "Without label",
                                            }}
                                            className="status-direct"
                                          >
                                            <MenuItem value="None">
                                              None
                                            </MenuItem>
                                            <MenuItem value="Private">
                                              Private
                                            </MenuItem>
                                            <MenuItem value="Foundation">
                                              Foundation
                                            </MenuItem>
                                            <MenuItem value="Insurance">
                                              Insurance
                                            </MenuItem>
                                            <MenuItem value="Insurance + Private">
                                              Insurance + Private
                                            </MenuItem>
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      <TableCell>
                                        <FormControl
                                          sx={{ m: 1, minWidth: 120 }}
                                          size="small"
                                          className="cont-main"
                                        >
                                          <Select
                                            value={
                                              normalizePatientStatus(
                                                info.p_status,
                                              ) || ""
                                            }
                                            onChange={(e) => {
                                              if (isReferral) return;
                                              handleChangefffff(
                                                e,
                                                info.patientId,
                                                info.patient_name ||
                                                  info.name ||
                                                  "",
                                              );
                                            }}
                                            disabled={isReferral}
                                            displayEmpty
                                            renderValue={(selected) =>
                                              getPatientStatusLabel(selected) ||
                                              selected ||
                                              "Select Status"
                                            }
                                            inputProps={{
                                              "aria-label": "Without label",
                                            }}
                                            className="status-direct"
                                            sx={
                                              isReferral
                                                ? {
                                                    "&.Mui-disabled": {
                                                      cursor: "not-allowed",
                                                      backgroundColor: "#f9f9f9",
                                                    },
                                                    "& .MuiSelect-select.Mui-disabled": {
                                                      WebkitTextFillColor: "var(--bs-text-color, #333)",
                                                      fontWeight: 600,
                                                      cursor: "not-allowed",
                                                    },
                                                  }
                                                : {}
                                            }
                                          >
                                            {PATIENT_STATUS_OPTIONS.map(
                                              (option) => (
                                                <MenuItem
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </MenuItem>
                                              ),
                                            )}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      <TableCell className="action-icon">
                                        {isReferral ? (
                                          <VisibilityIcon
                                            className="eye-icon"
                                            titleAccess="View Details"
                                            style={{ cursor: "pointer" }}
                                            onClick={(e) =>
                                              PatientDetail(
                                                e,
                                                info.patientId,
                                                info.enquiryId,
                                                info.id,
                                                info.user_id,
                                                null,
                                                info.createdAt,
                                              )
                                            }
                                          />
                                        ) : (
                                          <>
                                            <i
                                              className="fa-solid fa-notes-medical"
                                              title="Create Notes"
                                              style={{ cursor: "pointer" }}
                                              onClick={(e) =>
                                                handleOpenPatientNotes(
                                                  e,
                                                  info.patientId,
                                                )
                                              }
                                            ></i>
                                            <i
                                              className="fa-solid fa-comment"
                                              title="Chat with patient"
                                              style={{
                                                cursor: info.user_id
                                                  ? "pointer"
                                                  : "not-allowed",
                                                opacity: info.user_id ? 1 : 0.4,
                                                marginRight: 8,
                                              }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (!info.user_id) {
                                                  toast.error(
                                                    "Patient user id not found",
                                                  );
                                                  return;
                                                }
                                                navigate("/Admin/messages", {
                                                  state: {
                                                    patientUserId: String(
                                                      info.user_id,
                                                    ),
                                                    patientName:
                                                      info.patient_name ||
                                                      info.name ||
                                                      "Patient",
                                                  },
                                                });
                                              }}
                                            ></i>
                                            <VisibilityIcon
                                              className="eye-icon"
                                              onClick={(e) =>
                                                PatientDetail(
                                                  e,
                                                  info.patientId,
                                                  info.enquiryId,
                                                  info.id,
                                                  info.user_id,
                                                  null,
                                                  info.createdAt,
                                                )
                                              }
                                            />
                                            {canTrackActivity && (
                                              <i
                                                className="fa-solid fa-clock-rotate-left"
                                                title="Track Activity"
                                                onClick={(e) =>
                                                  handleOpenPatientActivity(
                                                    info.patientId,
                                                  )
                                                }
                                              ></i>
                                            )}
                                            {allowEdit && (
                                            <i
                                              className="fa-solid fa-pen-to-square"
                                              onClick={(e) =>
                                                EditButton(e, info.patientId, info)
                                              }
                                            ></i>
                                            )}
                                            {allowDelete ? (
                                              <i
                                                className="fa-solid fa-trash"
                                                onClick={(e) => {
                                                  handledelet(e, info.patientId);
                                                }}
                                              ></i>
                                            ) : (
                                              ""
                                            )}
                                          </>
                                        )}
                                      </TableCell>
                                    </>
                                  ) : (
                                    ""
                                  )}
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      )}
                    </Table>
                    {!pdfRowLimit && (
                      <Stack spacing={2}>
                        <Pagination
                          className="page-nation"
                          count={pagination.totalPages}
                          page={page}
                          onChange={(event, value) => setPage(value)}
                          color="primary"
                        />
                      </Stack>
                    )}
                  </TableContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <React.Fragment>
        <Dialog
          fullWidth={fullWidth}
          maxWidth={maxWidth}
          open={openPatientNotes}
          onClose={handleClosePatientNotes}
        >
          <div className="main-card-header">
            <div className="note-hd">
              <h6>Create Notes</h6>
            </div>
            <div className="cross-icon" onClick={handleClosePatientNotes}>
              <i className="fa-solid fa-xmark"></i>
            </div>
          </div>
          <DialogContent className="main-box">
            <Box
              noValidate
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "fit-content",
              }}
              className="contact-form"
            >
              <Box>
                <form id="patient-notes-form">
                  <div className="field-set">
                    <label>
                      Notes<span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="patientNote"
                      rows="4"
                      cols="50"
                      className="form-control"
                      placeholder="Note"
                      onChange={(e) => setPatientNote(e.target.value)}
                      value={patientNote}
                    />
                    <span style={{ color: "red" }}>
                      {patientNoteErr && !patientNote
                        ? "Please Enter Your  note"
                        : ""}
                    </span>
                  </div>
                  <div className="field-set">
                    <label>
                      Date<span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="date"
                      placeholder="Date"
                      className="form-control"
                      onChange={(e) => setPatientNoteDate(e.target.value)}
                      value={patientNoteDate}
                    />
                    <span style={{ color: "red" }}>
                      {patientNoteErr && !patientNoteDate
                        ? "Please Enter Your  date"
                        : ""}
                    </span>
                  </div>
                  <DialogActions className="submit-main">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={patientNoteSubmitting}
                      onClick={handleSubmitPatientNotes}
                    >
                      {patientNoteSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                  </DialogActions>
                </form>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </React.Fragment>

      <TravelStatusModal
        open={travelStatusModal.open}
        status={travelStatusModal.status}
        patientId={travelStatusModal.patientId}
        patientName={travelStatusModal.patientName}
        onClose={handleCloseTravelStatusModal}
        onSubmit={handleSubmitTravelStatus}
        submitting={travelStatusSubmitting}
      />

      <Dialog
        fullWidth
        maxWidth="md"
        open={activityOpen}
        onClose={handleClosePatientActivity}
        className="activity-log-dialog"
        sx={{ zIndex: 1300 }}
      >
        <div className="activity-log-header">
          <h6>Patient Activity Log</h6>
          <div className="d-flex align-items-center gap-2">
            {allowExport && (
            <button
              type="button"
              className="export-excel-btn"
              onClick={handleExportPatientActivityExcel}
              disabled={activityLoading || activityLogs.length === 0}
              title="Export to Excel"
            >
              <i className="fa fa-file-excel-o me-1"></i> Export Excel
            </button>
            )}
            <button className="close-btn" onClick={handleClosePatientActivity} type="button">
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>
        <DialogContent className="activity-log-content">
          <div className="activity-log-patient-info">
            <div><span>Patient ID:</span> {activityPatientId}</div>
            <div><span>Total Records:</span> {activityTotalRecords}</div>
          </div>
          {activityLoading ? (
            <Loader />
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e8e8e8", borderRadius: "6px" }}>
                <Table stickyHeader size="small" className="activity-log-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sr.No.</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Module</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Date/Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          No activity logs found
                        </TableCell>
                      </TableRow>
                    ) : (
                      activityLogs.map((log, idx) => (
                        <TableRow key={log?.id || idx}>
                          <TableCell>{(activityPage - 1) * 25 + idx + 1}</TableCell>
                          <TableCell title={log?.performedBy || "-"}>
                            {truncateCellText(log?.performedBy || "-", 22)}
                          </TableCell>
                          <TableCell title={log?.userRole || "-"}>
                            {truncateCellText(log?.userRole || "-", 15)}
                          </TableCell>
                          <TableCell title={log?.module || "-"}>
                            {truncateCellText(log?.module || "-", 18)}
                          </TableCell>
                          <TableCell title={log?.description || "-"}>
                            {truncateCellText(log?.description || "-", 40)}
                          </TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>
                            {log?.dateTime || "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!activityLoading && activityTotalPages > 1 && (
                <Stack spacing={2} sx={{ mt: 2, mb: 2 }}>
                  <Pagination
                    className="page-nation"
                    count={activityTotalPages}
                    page={activityPage}
                    onChange={handleActivityPageChange}
                    color="primary"
                  />
                </Stack>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
